import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../services/api';
import Pagination from '../../components/common/Pagination';

function LicenseManagementPage() {
    const [licenses, setLicenses] = useState([]);
    const [loading, setLoading] = useState(true);

    // 페이지네이션 상태
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    useEffect(() => {
        fetchLicenses();
    }, [page, size]);

    const fetchLicenses = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');

            // 라이선스 API가 없으면 회사 목록에서 라이선스 정보 추출
            const response = await fetch(`${getApiBaseUrl()}/platform/companies?page=${page}&size=${size}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const result = await response.json();
            if (result.success && result.data) {
                const data = result.data;
                // 회사 정보를 라이선스 형태로 변환
                const licenseData = (data.content || []).map(company => ({
                    id: company.id,
                    companyName: company.companyName,
                    licenseType: company.subscriptionPlan || 'STANDARD',
                    maxUsers: company.maxUsers || 10,
                    currentUsers: company.userCount || 0,
                    maxEquipments: company.maxEquipment || 20,
                    currentEquipments: company.equipmentCount || 0,
                    startDate: company.contractStartDate,
                    endDate: company.contractEndDate,
                    status: company.subscriptionStatus || (company.isActive ? 'ACTIVE' : 'INACTIVE')
                }));
                setLicenses(licenseData);
                setTotalElements(data.totalElements || 0);
                setTotalPages(data.totalPages || 0);
            }
        } catch (error) {
            console.error('라이선스 목록 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('ko-KR');
    };

    const getLicenseTypeLabel = (type) => {
        switch (type) {
            case 'ENTERPRISE': return '엔터프라이즈';
            case 'STANDARD': return '스탠다드';
            case 'BASIC': return '베이직';
            case 'TRIAL': return '체험판';
            default: return type || '스탠다드';
        }
    };

    const getLicenseTypeColor = (type) => {
        switch (type) {
            case 'ENTERPRISE': return '#f59e0b';
            case 'STANDARD': return '#3B82F6';
            case 'BASIC': return '#22C55E';
            case 'TRIAL': return '#9CA3AF';
            default: return '#3B82F6';
        }
    };

    const getDaysRemaining = (endDate) => {
        if (!endDate) return 999;
        const end = new Date(endDate);
        const today = new Date();
        const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
    };

    return (
        <div className="platform-page">
            <div className="platform-page-header">
                <h1 className="platform-page-title">라이선스 관리</h1>
                <p className="platform-page-description">회사별 라이선스를 관리합니다.</p>
            </div>

            {/* 통계 */}
            <div className="platform-stats-grid">
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">license</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">{totalElements}</div>
                        <div className="platform-stat-label">전체 라이선스</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">check_circle</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {licenses.filter(l => l.status === 'ACTIVE').length}
                        </div>
                        <div className="platform-stat-label">활성 라이선스</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">warning</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {licenses.filter(l => getDaysRemaining(l.endDate) <= 30 && getDaysRemaining(l.endDate) > 0).length}
                        </div>
                        <div className="platform-stat-label">만료 임박 (30일 이내)</div>
                    </div>
                </div>
            </div>

            {/* 라이선스 목록 */}
            <div className="platform-table-container">
                <div className="platform-table-header">
                    <h3 className="platform-table-title">라이선스 목록</h3>
                    <button className="platform-btn platform-btn-primary">
                        <span className="material-symbols-outlined">add</span>
                        라이선스 발급
                    </button>
                </div>
                {loading ? (
                    <div className="platform-empty">
                        <div className="platform-empty-text">로딩 중...</div>
                    </div>
                ) : licenses.length === 0 ? (
                    <div className="platform-empty">
                        <span className="material-symbols-outlined platform-empty-icon">license</span>
                        <div className="platform-empty-text">라이선스가 없습니다.</div>
                    </div>
                ) : (
                    <>
                        <table className="platform-table">
                            <thead>
                                <tr>
                                    <th>회사명</th>
                                    <th>라이선스 유형</th>
                                    <th>사용자</th>
                                    <th>설비</th>
                                    <th>시작일</th>
                                    <th>만료일</th>
                                    <th>상태</th>
                                    <th>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {licenses.map((license) => {
                                    const daysRemaining = getDaysRemaining(license.endDate);
                                    return (
                                        <tr key={license.id}>
                                            <td><strong>{license.companyName}</strong></td>
                                            <td>
                                                <span style={{
                                                    color: getLicenseTypeColor(license.licenseType),
                                                    fontWeight: 500
                                                }}>
                                                    {getLicenseTypeLabel(license.licenseType)}
                                                </span>
                                            </td>
                                            <td>
                                                {license.currentUsers} / {license.maxUsers}
                                                <div style={{
                                                    width: '60px',
                                                    height: '4px',
                                                    background: '#2a2f38',
                                                    borderRadius: '2px',
                                                    marginTop: '4px'
                                                }}>
                                                    <div style={{
                                                        width: `${Math.min((license.currentUsers / license.maxUsers) * 100, 100)}%`,
                                                        height: '100%',
                                                        background: license.currentUsers / license.maxUsers > 0.8 ? '#EF4444' : '#22C55E',
                                                        borderRadius: '2px'
                                                    }} />
                                                </div>
                                            </td>
                                            <td>
                                                {license.currentEquipments} / {license.maxEquipments}
                                                <div style={{
                                                    width: '60px',
                                                    height: '4px',
                                                    background: '#2a2f38',
                                                    borderRadius: '2px',
                                                    marginTop: '4px'
                                                }}>
                                                    <div style={{
                                                        width: `${Math.min((license.currentEquipments / license.maxEquipments) * 100, 100)}%`,
                                                        height: '100%',
                                                        background: license.currentEquipments / license.maxEquipments > 0.8 ? '#EF4444' : '#22C55E',
                                                        borderRadius: '2px'
                                                    }} />
                                                </div>
                                            </td>
                                            <td>{formatDate(license.startDate)}</td>
                                            <td>
                                                {formatDate(license.endDate)}
                                                {daysRemaining <= 30 && daysRemaining > 0 && (
                                                    <div style={{ fontSize: '11px', color: '#F59E0B', marginTop: '2px' }}>
                                                        {daysRemaining}일 남음
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`platform-badge ${license.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                                                    {license.status === 'ACTIVE' ? '활성' : '만료'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="platform-actions">
                                                    <button className="platform-action-btn edit" title="연장">
                                                        <span className="material-symbols-outlined">update</span>
                                                    </button>
                                                    <button className="platform-action-btn edit" title="수정">
                                                        <span className="material-symbols-outlined">edit</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            size={size}
                            onPageChange={handlePageChange}
                            onSizeChange={handleSizeChange}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default LicenseManagementPage;
